import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Lightbulb, Plus, Trash2, Type, Heading1, Heading2, AlignLeft, ListChecks,
  Link as LinkIcon, X, Loader2, ArrowLeft, FilePlus, Check, Circle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const uid = () => (window.crypto?.randomUUID?.() || `id-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`);
const GRID = 8;
const snap = (v) => Math.round(v / GRID) * GRID;

const DEFAULTS = {
  title:     { type: 'title',     w: 280, text: 'Título' },
  subtitle:  { type: 'subtitle',  w: 260, text: 'Subtítulo' },
  text:      { type: 'text',      w: 260, text: 'Escribe aquí tu idea...' },
  checklist: { type: 'checklist', w: 260, text: 'Lista', items: [{ id: uid(), text: 'Primer ítem', done: false }] },
};

const blockHeight = (b) => {
  if (b.type === 'title') return 80;
  if (b.type === 'subtitle') return 70;
  if (b.type === 'checklist') return Math.max(110, 50 + (b.items?.length || 0) * 32);
  return 110;
};

/* ============================ Block ============================ */
const Block = ({ block, isSelected, isConnectMode, onSelect, onChange, onDelete, onSpawnChild,
  onStartConnect, onFinishConnect, theme, onDragStart, onDrag }) => {
  const startConnect = (e) => {
    e.stopPropagation();
    onStartConnect(block.id);
  };

  const updateItem = (idx, patch) => {
    const items = (block.items || []).map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...block, items });
  };
  const addItem = () => {
    const items = [...(block.items || []), { id: uid(), text: '', done: false }];
    onChange({ ...block, items });
  };
  const removeItem = (idx) => {
    const items = (block.items || []).filter((_, i) => i !== idx);
    onChange({ ...block, items });
  };

  const h = block.h || blockHeight(block);

  return (
    <div
      className="absolute group"
      style={{
        left: block.x, top: block.y, width: block.w || 260, minHeight: h,
        zIndex: isSelected ? 20 : 10,
      }}
      onMouseDown={(e) => {
        // Click on block (not inputs) initiates drag
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
        e.stopPropagation();
        onSelect(block.id);
        onDragStart(block.id, e);
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      data-testid={`bs-block-${block.id}`}
    >
      <div
        className="rounded-xl p-3 transition-all cursor-grab active:cursor-grabbing"
        style={{
          background: theme.card,
          border: `2px solid ${isSelected ? theme.accent : theme.border}`,
          boxShadow: isSelected ? `0 8px 24px ${theme.accent}30` : `0 2px 6px rgba(0,0,0,0.2)`,
        }}
      >
        {/* Type badge */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>
            {block.type === 'title' && <Heading1 size={11} />}
            {block.type === 'subtitle' && <Heading2 size={11} />}
            {block.type === 'text' && <AlignLeft size={11} />}
            {block.type === 'checklist' && <ListChecks size={11} />}
            <span>{block.type}</span>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
            className="p-1 opacity-50 hover:opacity-100"
            style={{ color: theme.textMuted }}
            data-testid={`bs-block-delete-${block.id}`}>
            <X size={11} />
          </button>
        </div>

        {/* Content */}
        {block.type === 'title' && (
          <input
            value={block.text || ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="Título..."
            className="w-full bg-transparent outline-none text-xl font-medium"
            style={{ color: theme.textPrimary }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        {block.type === 'subtitle' && (
          <input
            value={block.text || ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="Subtítulo..."
            className="w-full bg-transparent outline-none text-base font-medium"
            style={{ color: theme.textSecondary }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        {block.type === 'text' && (
          <textarea
            value={block.text || ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            placeholder="Escribe..."
            className="w-full bg-transparent outline-none text-sm resize-none"
            style={{ color: theme.textPrimary, minHeight: 60 }}
            rows={3}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        {block.type === 'checklist' && (
          <div className="space-y-1" onMouseDown={(e) => e.stopPropagation()}>
            {(block.items || []).map((it, idx) => (
              <div key={it.id} className="flex items-center gap-2 group/item">
                <button onClick={() => updateItem(idx, { done: !it.done })}>
                  {it.done ? <Check size={13} style={{ color: theme.success }} /> : <Circle size={13} style={{ color: theme.textMuted }} />}
                </button>
                <input
                  value={it.text}
                  onChange={(e) => updateItem(idx, { text: e.target.value })}
                  placeholder="Ítem..."
                  className="flex-1 bg-transparent outline-none text-xs"
                  style={{
                    color: it.done ? theme.textMuted : theme.textPrimary,
                    textDecoration: it.done ? 'line-through' : 'none',
                  }}
                />
                <button onClick={() => removeItem(idx)}
                  className="opacity-0 group-hover/item:opacity-100" style={{ color: theme.textMuted }}>
                  <X size={10} />
                </button>
              </div>
            ))}
            <button onClick={addItem}
              className="text-[10px] inline-flex items-center gap-1 mt-1 hover:opacity-80"
              style={{ color: theme.accent }}>
              <Plus size={9} /> ítem
            </button>
          </div>
        )}
      </div>

      {/* Connection handle (right side) — click to spawn-and-connect child, double-click to manually link */}
      <div className="absolute -right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); onSpawnChild(block.id); }}
          className="w-6 h-6 rounded-full flex items-center justify-center shadow-md opacity-60 group-hover:opacity-100 transition"
          style={{ background: theme.accent, color: theme.bg }}
          title="Crear bloque conectado"
          data-testid={`bs-spawn-${block.id}`}>
          <Plus size={11} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); isConnectMode ? onFinishConnect(block.id) : startConnect(e); }}
          className="w-5 h-5 rounded-full flex items-center justify-center shadow-md opacity-50 group-hover:opacity-100 transition"
          style={{
            background: isConnectMode ? theme.accent : theme.elevated,
            color: isConnectMode ? theme.bg : theme.textMuted,
            border: `1px solid ${theme.border}`,
          }}
          title={isConnectMode ? 'Conectar a otro bloque' : 'Iniciar conexión manual'}
          data-testid={`bs-connect-${block.id}`}>
          <LinkIcon size={9} />
        </button>
      </div>
    </div>
  );
};

/* ============================ Boards list ============================ */
const BoardsList = ({ boards, onOpen, onCreate, onDelete, theme }) => (
  <div className="max-w-4xl mx-auto space-y-4">
    <header className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em]" style={{ color: theme.textMuted }}>Lluvia de ideas</p>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mt-1" style={{ color: theme.textPrimary }}>Tableros</h1>
      </div>
      <Button onClick={onCreate} className="inline-flex items-center gap-1.5"
        style={{ background: theme.accent, color: theme.bg }}
        data-testid="bs-new-board">
        <FilePlus size={14} /> Nuevo tablero
      </Button>
    </header>

    {boards.length === 0 ? (
      <div className="rounded-2xl text-center py-20 px-6"
        style={{ background: theme.card, border: `1px dashed ${theme.border}` }}>
        <Lightbulb size={36} className="mx-auto mb-3 opacity-50" style={{ color: theme.accent }} />
        <h3 className="text-lg font-medium mb-1" style={{ color: theme.textPrimary }}>Empieza una idea</h3>
        <p className="text-sm max-w-md mx-auto mb-5" style={{ color: theme.textSecondary }}>
          Crea bloques de título, subtítulo, texto y checklist. Conéctalos entre sí desde el botón + a la derecha de cada bloque.
        </p>
        <Button onClick={onCreate} className="inline-flex items-center gap-1.5"
          style={{ background: theme.accent, color: theme.bg }}>
          <FilePlus size={14} /> Crear mi primer tablero
        </Button>
      </div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {boards.map((b) => (
          <div key={b.id} className="rounded-2xl p-4 group hover:scale-[1.01] transition-transform cursor-pointer"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}
            onClick={() => onOpen(b.id)}
            data-testid={`bs-card-${b.id}`}>
            <Lightbulb size={16} className="mb-2" style={{ color: theme.accent }} />
            <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{b.title || 'Sin título'}</p>
            <p className="text-[11px] mt-1" style={{ color: theme.textMuted }}>
              {b.blocks?.length || 0} bloque{(b.blocks?.length || 0) === 1 ? '' : 's'} ·{' '}
              {new Date(b.updated_at).toLocaleDateString()}
            </p>
            <button onClick={(e) => { e.stopPropagation(); onDelete(b.id); }}
              className="absolute opacity-0 group-hover:opacity-60 hover:!opacity-100 p-1.5"
              style={{ color: theme.error, marginTop: -2 }}>
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* ============================ Editor ============================ */
const BoardEditor = ({ board, onBack, onUpdate, onDelete, theme }) => {
  const [local, setLocal] = useState(board);
  const [selectedId, setSelectedId] = useState(null);
  const [connectFromId, setConnectFromId] = useState(null);  // when set, next block click creates an edge
  const canvasRef = useRef(null);
  const saveTimer = useRef(null);
  const drag = useRef(null); // { id, dx, dy }

  // Debounced save
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onUpdate(local).catch(() => {});
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.title, local.blocks, local.edges]);

  const patchBlock = useCallback((id, patch) => {
    setLocal((p) => ({ ...p, blocks: p.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  }, []);

  const deleteBlock = useCallback((id) => {
    setLocal((p) => ({
      ...p,
      blocks: p.blocks.filter((b) => b.id !== id),
      edges: p.edges.filter((e) => e.from_id !== id && e.to_id !== id),
    }));
    setSelectedId(null);
  }, []);

  const addBlock = useCallback((type, position = null) => {
    const def = DEFAULTS[type] || DEFAULTS.text;
    const id = uid();
    const rect = canvasRef.current?.getBoundingClientRect();
    const x = position?.x ?? snap((rect?.width || 800) / 2 - 130);
    const y = position?.y ?? snap((rect?.height || 600) / 2 - 60);
    const newBlock = { id, ...def, x, y };
    if (type === 'checklist') newBlock.items = [{ id: uid(), text: 'Primer ítem', done: false }];
    setLocal((p) => ({ ...p, blocks: [...p.blocks, newBlock] }));
    setSelectedId(id);
    return newBlock;
  }, []);

  const spawnChildOf = useCallback((parentId) => {
    setLocal((p) => {
      const parent = p.blocks.find((b) => b.id === parentId);
      if (!parent) return p;
      const id = uid();
      const def = DEFAULTS.text;
      const child = {
        id, ...def,
        x: snap(parent.x + (parent.w || 260) + 60),
        y: snap(parent.y),
      };
      const edge = { id: uid(), from_id: parentId, to_id: id };
      return { ...p, blocks: [...p.blocks, child], edges: [...p.edges, edge] };
    });
  }, []);

  const startConnect = (id) => {
    setConnectFromId(id);
    toast('Selecciona el segundo bloque para conectar.', { icon: '🔗' });
  };
  const finishConnect = (toId) => {
    if (!connectFromId || connectFromId === toId) { setConnectFromId(null); return; }
    setLocal((p) => {
      // Avoid duplicates
      if (p.edges.some((e) => e.from_id === connectFromId && e.to_id === toId)) return p;
      return { ...p, edges: [...p.edges, { id: uid(), from_id: connectFromId, to_id: toId }] };
    });
    setConnectFromId(null);
    toast.success('Conectado');
  };

  // Drag-to-move blocks
  const onBlockDragStart = (id, e) => {
    const block = local.blocks.find((b) => b.id === id);
    if (!block) return;
    drag.current = {
      id,
      startMouseX: e.clientX, startMouseY: e.clientY,
      startBlockX: block.x, startBlockY: block.y,
    };
    window.addEventListener('mousemove', onBlockDragMove);
    window.addEventListener('mouseup', onBlockDragEnd);
  };
  const onBlockDragMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startMouseX;
    const dy = e.clientY - drag.current.startMouseY;
    patchBlock(drag.current.id, {
      x: snap(drag.current.startBlockX + dx),
      y: snap(drag.current.startBlockY + dy),
    });
  };
  const onBlockDragEnd = () => {
    drag.current = null;
    window.removeEventListener('mousemove', onBlockDragMove);
    window.removeEventListener('mouseup', onBlockDragEnd);
  };

  // Edges (SVG path) — flexible routing: picks the closest side of source→target so the line
  // exits/enters where the block actually sits (right-to-left, left-to-right, top-to-bottom, etc.)
  const edgePaths = useMemo(() => local.edges.map((e) => {
    const a = local.blocks.find((b) => b.id === e.from_id);
    const b = local.blocks.find((bb) => bb.id === e.to_id);
    if (!a || !b) return null;
    const aw = a.w || 260, ah = a.h || blockHeight(a);
    const bw = b.w || 260, bh = b.h || blockHeight(b);
    const acx = a.x + aw / 2, acy = a.y + ah / 2;
    const bcx = b.x + bw / 2, bcy = b.y + bh / 2;
    const dx = bcx - acx, dy = bcy - acy;

    // Pick side based on the dominant delta between block centers
    let ax, ay, bx, by, orientation;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Horizontal routing
      if (dx >= 0) { ax = a.x + aw;   bx = b.x;      } // A → B (right → left)
      else         { ax = a.x;        bx = b.x + bw; } // A ← B (left → right)
      ay = acy; by = bcy;
      orientation = 'h';
    } else {
      // Vertical routing
      if (dy >= 0) { ay = a.y + ah;   by = b.y;      } // A ↓ B (bottom → top)
      else         { ay = a.y;        by = b.y + bh; } // A ↑ B (top → bottom)
      ax = acx; bx = bcx;
      orientation = 'v';
    }

    // Bezier control points aligned with the exit direction (smooth S-curve)
    const c = 60;
    let c1x = ax, c1y = ay, c2x = bx, c2y = by;
    if (orientation === 'h') {
      const sign = dx >= 0 ? 1 : -1;
      c1x = ax + sign * c;
      c2x = bx - sign * c;
    } else {
      const sign = dy >= 0 ? 1 : -1;
      c1y = ay + sign * c;
      c2y = by - sign * c;
    }

    return { id: e.id, path: `M ${ax} ${ay} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${bx} ${by}` };
  }).filter(Boolean), [local.blocks, local.edges]);

  const removeEdge = (id) => setLocal((p) => ({ ...p, edges: p.edges.filter((e) => e.id !== id) }));

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-3 sm:px-5 py-3 flex-wrap"
        style={{ borderBottom: `1px solid ${theme.border}`, background: theme.card }}>
        <button onClick={onBack} className="p-1.5 rounded hover:opacity-80" style={{ color: theme.textMuted }}>
          <ArrowLeft size={16} />
        </button>
        <input
          value={local.title}
          onChange={(e) => setLocal({ ...local, title: e.target.value })}
          placeholder="Nombre del tablero..."
          className="bg-transparent outline-none text-base font-medium flex-1 min-w-[120px]"
          style={{ color: theme.textPrimary }}
        />
        <div className="flex items-center gap-1 ml-auto">
          {[
            { type: 'title',     label: 'Título',    icon: Heading1 },
            { type: 'subtitle',  label: 'Subtítulo', icon: Heading2 },
            { type: 'text',      label: 'Texto',     icon: AlignLeft },
            { type: 'checklist', label: 'Lista',     icon: ListChecks },
          ].map(({ type, label, icon: Icon }) => (
            <button key={type} onClick={() => addBlock(type)}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1.5 rounded-md"
              style={{ background: theme.elevated, color: theme.textSecondary, border: `1px solid ${theme.border}` }}
              data-testid={`bs-add-${type}`}>
              <Icon size={11} /> {label}
            </button>
          ))}
          <button onClick={() => onDelete(local.id)}
            className="ml-2 text-[11px] inline-flex items-center gap-1 px-2 py-1.5 rounded-md"
            style={{ color: theme.error }}
            title="Eliminar tablero">
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="relative flex-1 overflow-auto"
        style={{
          background: theme.bg,
          backgroundImage: `radial-gradient(${theme.border} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          minHeight: 'calc(100vh - 220px)',
        }}
        onClick={() => { setSelectedId(null); setConnectFromId(null); }}
        data-testid="bs-canvas"
      >
        {/* Edges layer */}
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ width: '100%', height: '100%', minWidth: 2000, minHeight: 2000 }}
        >
          {edgePaths.map((e) => (
            <g key={e.id} className="pointer-events-auto">
              <path d={e.path} fill="none" stroke={theme.accent} strokeOpacity={0.55} strokeWidth={2} />
              {/* Click on edge to remove */}
              <path d={e.path} fill="none" stroke="transparent" strokeWidth={14}
                style={{ cursor: 'pointer' }}
                onClick={(ev) => { ev.stopPropagation(); if (window.confirm('¿Eliminar esta conexión?')) removeEdge(e.id); }} />
            </g>
          ))}
        </svg>

        {/* Blocks */}
        {local.blocks.map((b) => (
          <Block
            key={b.id}
            block={b}
            isSelected={selectedId === b.id}
            isConnectMode={!!connectFromId && connectFromId !== b.id}
            theme={theme}
            onSelect={setSelectedId}
            onChange={(next) => patchBlock(b.id, next)}
            onDelete={deleteBlock}
            onSpawnChild={spawnChildOf}
            onStartConnect={startConnect}
            onFinishConnect={finishConnect}
            onDragStart={onBlockDragStart}
          />
        ))}

        {/* Empty state */}
        {local.blocks.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <Lightbulb size={36} className="mb-2 opacity-40" style={{ color: theme.accent }} />
            <p className="text-sm" style={{ color: theme.textMuted }}>
              Agrega un bloque desde la barra superior para empezar.
            </p>
          </div>
        )}

        {/* Connect-mode hint */}
        {connectFromId && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
            style={{ background: theme.accent, color: theme.bg }}>
            <LinkIcon size={12} /> Modo conexión · clic en otro bloque para enlazar
            <button onClick={() => setConnectFromId(null)} className="ml-2 underline text-[11px]">Cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================ Page ============================ */
const Brainstorm = () => {
  const { theme } = useTheme();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently opened board

  useEffect(() => {
    api.get('/brainstorm')
      .then((r) => setBoards(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('No pude cargar los tableros.'))
      .finally(() => setLoading(false));
  }, []);

  const createBoard = async () => {
    try {
      const { data } = await api.post('/brainstorm', { title: 'Nuevo tablero', blocks: [], edges: [] });
      setBoards((p) => [data, ...p]);
      setActive(data);
    } catch { toast.error('No pude crear el tablero.'); }
  };

  const openBoard = async (id) => {
    try {
      const { data } = await api.get(`/brainstorm/${id}`);
      setActive(data);
    } catch { toast.error('No pude abrir el tablero.'); }
  };

  const updateBoard = async (next) => {
    setBoards((p) => p.map((b) => (b.id === next.id ? { ...b, ...next, updated_at: new Date().toISOString() } : b)));
    setActive((cur) => (cur?.id === next.id ? next : cur));
    await api.put(`/brainstorm/${next.id}`, {
      title: next.title, blocks: next.blocks, edges: next.edges,
    });
  };

  const deleteBoard = async (id) => {
    if (!window.confirm('¿Eliminar este tablero y todos sus bloques?')) return;
    try {
      await api.delete(`/brainstorm/${id}`);
      setBoards((p) => p.filter((b) => b.id !== id));
      if (active?.id === id) setActive(null);
    } catch { toast.error('No pude eliminar.'); }
  };

  if (loading) {
    return (
      <div className="text-center py-16" style={{ color: theme.textMuted }}>
        <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Cargando tableros...
      </div>
    );
  }

  if (active) {
    return (
      <div className="-mx-3 sm:-mx-6 lg:-mx-8 -my-6">
        <BoardEditor
          board={active}
          onBack={() => setActive(null)}
          onUpdate={updateBoard}
          onDelete={deleteBoard}
          theme={theme}
        />
      </div>
    );
  }

  return (
    <BoardsList
      boards={boards}
      onOpen={openBoard}
      onCreate={createBoard}
      onDelete={deleteBoard}
      theme={theme}
    />
  );
};

export default Brainstorm;
